/**
 * Coach home (E4/E5/A2): roster overview with at-risk flags, weekly digest,
 * student invitations and the onboarding progress banner.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../../lib/api.ts';
import { Button, Card, EmptyState, ErrorText, Input, Modal, Spinner, Tag, fmtDate } from '../../lib/ui.tsx';

interface RosterRow {
  student: { id: string; name: string; email: string };
  voiceType: string | null;
  practicedThisWeek: boolean;
  sessionsThisWeek: number;
  lastActivity: string | null;
  pendingSubmissions: number;
  streak: number;
  atRisk: boolean;
}

interface Digest {
  practiced: number;
  total: number;
  waiting: number;
  quiet: number;
  text: string;
}

interface Invite {
  id: string;
  email: string | null;
  status: string;
  createdAt: string;
  link: string;
}

interface OnboardingState {
  done: boolean;
  invitesSent: number;
  studentsJoined: number;
  plansAssigned: number;
  activated: boolean;
}

export default function DashboardPage() {
  const [inviteOpen, setInviteOpen] = useState(false);

  const roster = useQuery({
    queryKey: ['roster'],
    queryFn: () => api<RosterRow[]>('/api/analytics/roster'),
  });
  const digest = useQuery({
    queryKey: ['digest'],
    queryFn: () => api<Digest>('/api/analytics/digest'),
  });
  const onboarding = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => api<OnboardingState>('/api/onboarding'),
  });

  const sendDigest = useMutation({
    mutationFn: () => api('/api/analytics/digest/send', { method: 'POST' }),
  });

  const rows = [...(roster.data ?? [])].sort((a, b) => Number(b.atRisk) - Number(a.atRisk));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Students</h1>
        <Button onClick={() => setInviteOpen(true)}>+ Invite students</Button>
      </div>

      {onboarding.data && !onboarding.data.done && (
        <Card className="border-brand-200 bg-brand-50">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-brand-900">Finish setting up your studio</p>
              <p className="mt-1 text-xs text-brand-800">
                {Math.min(onboarding.data.invitesSent, 3)}/3 invites sent · {Math.min(onboarding.data.plansAssigned, 1)}
                /1 plan assigned
              </p>
            </div>
            <Link to="/coach/onboarding">
              <Button variant="secondary">Continue setup →</Button>
            </Link>
          </div>
        </Card>
      )}

      <Card
        title="Weekly digest"
        actions={
          <Button variant="secondary" onClick={() => sendDigest.mutate()} disabled={sendDigest.isPending}>
            {sendDigest.isSuccess ? 'Sent ✓' : 'Email me this now'}
          </Button>
        }
      >
        {digest.isLoading ? (
          <Spinner />
        ) : digest.error ? (
          <ErrorText error={digest.error} />
        ) : (
          <p className="text-sm text-slate-600">{digest.data?.text}</p>
        )}
        <ErrorText error={sendDigest.error} />
      </Card>

      {roster.isLoading ? (
        <Spinner />
      ) : roster.error ? (
        <ErrorText error={roster.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No active students yet"
          hint="Invite your first students — they'll show up here with their practice at a glance."
          action={<Button onClick={() => setInviteOpen(true)}>Invite students</Button>}
        />
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <Link key={row.student.id} to={`/coach/students/${row.student.id}`} className="block">
              <Card className={row.atRisk ? 'border-red-200' : ''}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">
                      {row.student.name}
                      {row.voiceType && <span className="font-normal text-slate-400"> · {row.voiceType}</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {row.sessionsThisWeek} session{row.sessionsThisWeek === 1 ? '' : 's'} this week · last active{' '}
                      {fmtDate(row.lastActivity)} · 🔥 {row.streak}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {row.pendingSubmissions > 0 && <Tag tone="brand">{row.pendingSubmissions} to review</Tag>}
                    {row.atRisk ? (
                      <Tag tone="red">at risk</Tag>
                    ) : row.practicedThisWeek ? (
                      <Tag tone="green">practiced</Tag>
                    ) : (
                      <Tag tone="amber">quiet</Tag>
                    )}
                    <span className="text-brand-600">→</span>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}

function InviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [lastInvite, setLastInvite] = useState<Invite | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const invites = useQuery({
    queryKey: ['invites'],
    queryFn: () => api<Invite[]>('/api/auth/invites'),
    enabled: open,
  });

  const createInvite = useMutation({
    mutationFn: (inviteEmail?: string) =>
      api<Invite>('/api/auth/invites', { body: inviteEmail ? { email: inviteEmail } : {} }),
    onSuccess: (invite) => {
      setLastInvite(invite);
      setEmail('');
      void qc.invalidateQueries({ queryKey: ['invites'] });
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/auth/invites/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['invites'] }),
  });

  function copy(id: string, link: string) {
    void navigator.clipboard.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <Modal open={open} onClose={onClose} title="Invite students">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          createInvite.mutate(email.trim() || undefined);
        }}
        className="flex gap-2"
      >
        <Input
          type="email"
          placeholder="student@email.com (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button type="submit" disabled={createInvite.isPending} className="shrink-0">
          Invite
        </Button>
      </form>
      <p className="mt-1 text-xs text-slate-500">
        Leave the email empty to create a shareable join link. Students beyond your free allowance add to
        next month's per-student charge — nothing gets blocked.
      </p>
      <ErrorText error={createInvite.error} />

      {lastInvite && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3">
          <p className="text-xs text-slate-500">Share this link with your student:</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate text-xs text-slate-700">{lastInvite.link}</code>
            <Button variant="secondary" onClick={() => copy(lastInvite.id, lastInvite.link)} className="shrink-0">
              {copiedId === lastInvite.id ? 'Copied ✓' : 'Copy'}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        <p className="text-xs font-medium tracking-wide text-slate-400 uppercase">Sent invitations</p>
        {invites.isLoading ? (
          <Spinner />
        ) : !invites.data || invites.data.length === 0 ? (
          <p className="text-xs text-slate-400">No invitations yet.</p>
        ) : (
          invites.data.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-slate-700">{inv.email ?? 'Shareable link'}</p>
                <p className="text-xs text-slate-400">{fmtDate(inv.createdAt)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Tag tone={inv.status === 'accepted' ? 'green' : inv.status === 'pending' ? 'amber' : 'default'}>
                  {inv.status}
                </Tag>
                {inv.status === 'pending' && (
                  <>
                    <Button variant="ghost" className="px-2 py-1" onClick={() => copy(inv.id, inv.link)}>
                      {copiedId === inv.id ? 'Copied ✓' : 'Copy'}
                    </Button>
                    <Button variant="ghost" className="px-2 py-1 text-red-600" onClick={() => revoke.mutate(inv.id)}>
                      Revoke
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
